/**
 * ----- Frontend Models -----
 * Types and interfaces used specifically in the frontend components and hooks.
 * These extend or transform API types for frontend usage.
 */

import { ContentTypeValues, MaterialProperty, Method } from "./api";

/**
 * Represents the roles of a chat message
 * @constant
 */
export const MessageRoles = {
  User: "user",
  Assistant: "assistant",
} as const;

/**
 * Represents the type of a source either coming from the backend or selected by the users
 * @constant
 */
export const SourceTypeValues = {
  ScientificSource: ContentTypeValues.ScientificSource,
  MateriomFormulation: ContentTypeValues.MateriomFormulation,
} as const;

/**
 * Type of source either coming from the backend or selected by the users
 */
export type SourceType =
  | typeof SourceTypeValues.ScientificSource
  | typeof SourceTypeValues.MateriomFormulation;

/**
 * Represents a source either retrieved or uploaded by the user
 * Used for the frontend components and hooks.
 * @interface Source
 * @property {SourceType} type - The type of source
 * @property {string} id - The ID of the source
 */
export interface Source {
  type: SourceType;
  id: string;
}

/**
 * Represents a chat message in the conversation
 * @interface ChatMessage
 * @property {MessageRoles} role - The sender of the message
 * @property {string} content - The content of the message
 * @property {string} [traceId] - Optional trace identifier
 * @property {boolean} [isProgress] - Optional progress flag
 */
export interface ChatMessage {
  role: typeof MessageRoles[keyof typeof MessageRoles];
  content: string;
  traceId?: string;
  id?: string;
  isProgress?: boolean;
  sources?: Source[]; // TODO: Add sources to chat as part of the stream processing
  // This will make it easier to render the sources in the chat message component
  // and will also make it easier to add sources to the chat history for the LLM
  // to consume.
}

/**
 * Represents a scientific source with title, abstract, and publication details
 * @interface ScientificSource
 * @property {SourceType} type - The type of source, which is SourceTypeValues.ScientificSource
 * @property {string} title - The title of the scientific source
 * @property {string} abstract - A brief description of the source
 * @property {string} url - The URL of the source
 * @property {number} year - The year of publication
 * @property {string} venue - The venue where published
 * @property {string} [imageUrl] - Optional image URL for the source
 */
export interface ScientificSource extends Source {
  title: string;
  abstract: string;
  url: string;
  year: number;
  venue: string;
  imageUrl?: string;
}

/**
 * Represents a Materiom commons recipe source
 * @interface MateriomFormulationSource
 * @property {string} url - The URL of the recipe
 * @property {string} name - The name of the recipe
 * @property {string[]} ingredients - The ingredients of the recipe
 * @property {number[]} ingredient_concentrations - The concentrations of the ingredients
 * @property {string[]} ingredient_units - The units of the ingredients
 * @property {string[]} author_ids - The author IDs of the recipe
 * @property {string[]} author_names - The names of the authors
 * @property {string[]} author_last_names - The last names of the authors
 * @property {string[]} contributor_ids - The contributor IDs of the recipe
 * @property {string[]} contributor_names - The names of the contributors
 * @property {string[]} contributor_last_names - The last names of the contributors
 * @property {Method[]} methods - The methods of the recipe
 * @property {MaterialProperty[]} material_properties - The material properties of the recipe
 */
export interface MateriomFormulationSource extends Source {
  url: string;
  name: string;
  ingredients: string[];
  ingredient_concentrations: number[];
  ingredient_units: string[];
  author_ids: string[];
  author_names: string[];
  author_last_names: string[] | null;
  contributor_ids: string[] | null;
  contributor_names: string[] | null;
  contributor_last_names: string[] | null;
  methods: Method[] | null;
  material_properties: MaterialProperty[] | null;
}

/**
 * Represents the category of a starter prompt
 * @enum PromptCategory
 * @property {PromptCategory} AboutChatbot - The category for the "What are you useful for?" prompt
 * @property {PromptCategory} MateriomAi - The category for the "Materiom AI" prompts
 */
export enum PromptCategory {
  AboutChatbot = "about_chatbot",
  MateriomAi = "materiom_ai",
}

/**
 * Represents a starter prompt
 * @interface Prompt
 * @property {string} message - The message of the prompt
 * @property {string} label - The label of the prompt
 * @property {PromptCategory} category - The category of the prompt
 */
export interface Prompt {
  message: string;
  label: string;
  category: PromptCategory;
}

/**
 * Represents the reaction to a chat message
 * @enum FeedbackReaction
 * @property {FeedbackReaction} Up - The reaction for a thumbs up
 * @property {FeedbackReaction} Down - The reaction for a thumbs down
 */
export enum FeedbackReaction {
  Up = "up",
  Down = "down",
}

/**
 * Represents feedback for a chat message.
 * @interface ChatMessageFeedback
 * @property {string} trace_id - The trace ID associated with the message.
 * @property {string[]} reasons - An array of reasons for the feedback.
 * @property {string} [comment] - An optional comment providing additional context.
 * @property {number} score - A numerical score representing the feedback.
 */
export interface ChatMessageFeedback {
  trace_id: string;
  reasons: string[];
  comment?: string;
  score: number;
}

/**
 * Represents a user
 * @interface User
 * @property {string} _id - The ID of the user.
 * @property {string} name - The name of the user.
 * @property {string} last_name - The last name of the user.
 * @property {string} institute - The institute of the user.
 * @property {string} [image] - The image of the user.
 */
export interface User {
  _id: string;
  name: string;
  last_name: string;
  institute: string;
  image?: string;
  email_verified_at?: string | null;
}
